import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MessagesSquare, Send, Loader2, Search } from "lucide-react";

export default function Comunicaciones() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadContacts = async (retry = true) => {
    try {
      const { data } = await api.get("/messages/contacts");
      const list = Array.isArray(data) ? data : [];
      if (list.length === 0 && retry) {
        setTimeout(() => loadContacts(false), 900); // reintento por posible carrera con el token
        return;
      }
      setContacts(list);
    } catch {
      if (retry) setTimeout(() => loadContacts(false), 900);
    }
  };
  useEffect(() => { loadContacts(); }, []);

  const openThread = async (c) => {
    setActive(c);
    try {
      const { data } = await api.get(`/messages/thread/${c.id}`);
      setMsgs(Array.isArray(data) ? data : []);
      setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)));
    } catch { setMsgs([]); }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // Polling del hilo activo.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/messages/thread/${active.id}`);
        setMsgs(Array.isArray(data) ? data : []);
      } catch { /* noop */ }
    }, 8000);
    return () => clearInterval(t);
  }, [active?.id]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { to_id: active.id, body: text.trim() });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch { /* noop */ } finally { setSending(false); }
  };

  const filtered = contacts.filter((c) => (c.name || "").toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="space-y-4" data-testid="comunicaciones-page">
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Comunicaciones</p>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
          <MessagesSquare className="text-[#A43790]" /> Mensajería
        </h1>
      </div>

      <div className="jasq-card overflow-hidden grid md:grid-cols-[300px_1fr]" style={{ height: "calc(100vh - 230px)", minHeight: 420 }}>
        {/* Contactos */}
        <div className="border-r border-[#E1E5F0] flex flex-col min-h-0" data-testid="contacts-pane">
          <div className="p-3 border-b border-[#E1E5F0]">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6ACC1]" />
              <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm focus:border-[#A43790] outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#6E7591] text-center p-6">No hay contactos.</p>
            ) : filtered.map((c) => (
              <button key={c.id} onClick={() => openThread(c)} data-testid={`contact-${c.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-[#F2F4FA] transition-colors ${active?.id === c.id ? "bg-[#FFF7DA]" : "hover:bg-[#F7F8FA]"}`}>
                <img src={c.avatar || "/avatars/owl-classic.png"} alt="" className="w-9 h-9 rounded-full object-cover bg-[#F2F4FA] shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block font-bold text-sm text-[#1F2547] truncate">{c.name}</span>
                  <span className="block text-[11px] text-[#A6ACC1] capitalize">{c.role}</span>
                </span>
                {c.unread > 0 && <span className="text-[10px] font-bold bg-[#D63384] text-white rounded-full px-1.5 py-0.5" data-testid={`unread-${c.id}`}>{c.unread}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Conversación */}
        <div className="flex flex-col min-h-0" data-testid="thread-pane">
          {!active ? (
            <div className="flex-1 grid place-items-center text-sm text-[#6E7591] p-6">Elige un contacto para conversar.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#E1E5F0] flex items-center gap-3">
                <img src={active.avatar || "/avatars/owl-classic.png"} alt="" className="w-8 h-8 rounded-full object-cover bg-[#F2F4FA]" />
                <span className="font-bold text-[#1F2547]">{active.name}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAFBFE]" data-testid="messages-list">
                {msgs.length === 0 && <p className="text-center text-sm text-[#A6ACC1] mt-6">Aún no hay mensajes. ¡Escribe el primero!</p>}
                {msgs.map((m) => {
                  const mine = m.from_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#1F2547] text-white rounded-br-sm" : "bg-white border border-[#E1E5F0] text-[#1F2547] rounded-bl-sm"}`}>
                        {m.body}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-[#E1E5F0] flex gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Escribe un mensaje…"
                  className="flex-1 px-3 py-2.5 rounded-2xl border-2 border-[#E1E5F0] text-sm focus:border-[#A43790] outline-none" />
                <button onClick={send} disabled={sending || !text.trim()} data-testid="send-message"
                  className="w-11 h-11 grid place-items-center rounded-2xl bg-[#A43790] text-white hover:bg-[#D63384] transition-colors disabled:opacity-50">
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
